/* KYO Reviews: filters, sorting, search, "load more", helpful votes and the video modal for one
   <kyo-reviews> section. Everything is scoped to the element, so several sections never touch each other.
   No dependencies, no tracking. */
(() => {
  if (customElements.get('kyo-reviews')) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fold = text => String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const store = {
    get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch (e) {} },
  };

  class KyoReviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.grid = this.querySelector('[data-kr-grid]');
      this.cards = [...this.grid.querySelectorAll('[data-kr-card]')];
      this.initial = Number(this.dataset.initial) || 6;
      this.step = Number(this.dataset.step) || 6;
      this.paginate = this.dataset.paginate === '1';
      this.limit = this.initial;
      this.state = { types: new Set(), star: '', query: '', sort: 'newest' };
      this.status = this.querySelector('[data-kr-status]');
      this.empty = this.querySelector('[data-kr-empty]');
      this.more = this.querySelector('[data-kr-more]');
      this.dialog = this.querySelector('[data-kr-dialog]');
      this.restoreHelpful();
      this.formatDates();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      let timer = 0;
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-kr-search]')) return;
        clearTimeout(timer);
        timer = setTimeout(() => { this.state.query = fold(event.target.value.trim()); this.limit = this.initial; this.apply(); }, 150);
      });
      this.dialog?.addEventListener('close', () => this.closeVideo());
      this.formDialog = this.querySelector('[data-kr-form-dialog]');
      // After Shopify's contact form posts, the page reloads: reopen on the thank-you or the errors.
      if (this.formDialog?.querySelector('[data-kr-form-posted], [data-kr-form-error]')) {
        this.scrollIntoView({ block: 'start' });
        this.formDialog.showModal();
      }
      this.shareStats();
      this.setupApi();
      this.setupUploads();
      this.setupCaptcha();
      this.openFromLink();
      this.onBlockSelect = event => { if (this.contains(event.target)) this.reveal(event.target.closest('[data-kr-card]')); };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.observeEntrance();
      this.apply(true);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.observer?.disconnect();
    }

    onClick(event) {
      const chip = event.target.closest('[data-kr-filter]');
      if (chip) {
        const type = chip.dataset.krFilter;
        if (type === 'all') this.resetFilters(false);
        else if (this.state.types.has(type)) this.state.types.delete(type);
        else this.state.types.add(type);
        this.limit = this.initial;
        this.apply();
        return;
      }
      const bar = event.target.closest('[data-kr-star]');
      if (bar) {
        this.state.star = this.state.star === bar.dataset.krStar ? '' : bar.dataset.krStar;
        this.limit = this.initial;
        this.apply();
        return;
      }
      if (event.target.closest('[data-kr-reset]')) { this.resetFilters(true); return; }
      if (event.target.closest('[data-kr-more]')) { this.loadMore(); return; }
      const helpful = event.target.closest('[data-kr-helpful]');
      if (helpful) { this.vote(helpful); return; }
      const write = event.target.closest('[data-kr-write]');
      if (write) { this.openForm(write); return; }
      if (this.formDialog && (event.target === this.formDialog || event.target.closest('[data-kr-form-close]'))) {
        this.formDialog.close();
        this.writeOpener?.focus({ preventScroll: true });
        return;
      }
      const video = event.target.closest('[data-kr-video-open]');
      if (video) { this.openVideo(video); return; }
      if (this.dialog && (event.target === this.dialog || event.target.closest('[data-kr-dialog-close]'))) this.dialog.close();
    }

    onChange(event) {
      if (event.target.matches('[data-kr-sort]')) this.state.sort = event.target.value;
      else if (event.target.matches('[data-kr-rating]')) this.state.star = event.target.value;
      else return;
      this.limit = this.initial;
      this.apply();
    }

    resetFilters(focus) {
      this.state.types.clear();
      this.state.star = '';
      this.state.query = '';
      const search = this.querySelector('[data-kr-search]');
      if (search) search.value = '';
      this.limit = this.initial;
      this.apply();
      if (focus) this.querySelector('[data-kr-filter="all"]')?.focus();
    }

    isMatch(card) {
      const { types, star, query } = this.state;
      const filtering = types.size || star || query;
      if (card.hasAttribute('data-kr-editor')) return !filtering;
      if (types.has('photo') && !card.dataset.photo) return false;
      if (types.has('video') && !card.dataset.video) return false;
      if (types.has('verified') && !card.dataset.verified) return false;
      if (star && card.dataset.rating !== star) return false;
      if (query && !fold(card.dataset.search).includes(query)) return false;
      return true;
    }

    compare(a, b) {
      const index = Number(a.dataset.index) - Number(b.dataset.index);
      const editor = a.hasAttribute('data-kr-editor') - b.hasAttribute('data-kr-editor');
      if (editor) return editor;
      const rating = Number(b.dataset.rating) - Number(a.dataset.rating);
      switch (this.state.sort) {
        case 'highest': return rating || index;
        case 'lowest': return -rating || index;
        case 'helpful': return (Number(b.dataset.helpful) - Number(a.dataset.helpful)) || index;
        default: {
          const da = a.dataset.date, db = b.dataset.date;
          if (da && db && da !== db) return this.state.sort === 'oldest' ? (da < db ? -1 : 1) : (da < db ? 1 : -1);
          if (da && !db) return -1;
          if (!da && db) return 1;
          return index;
        }
      }
    }

    apply(first) {
      const sorted = [...this.cards].sort(this.compare.bind(this));
      const shown = sorted.filter(card => this.isMatch(card));
      sorted.forEach(card => this.grid.append(card));
      const limit = this.paginate ? this.limit : Infinity;
      this.cards.forEach(card => { card.hidden = true; });
      shown.forEach((card, i) => { card.hidden = i >= limit; });
      this.total = shown.length;
      if (this.more) this.more.hidden = shown.length <= limit;
      this.classList.toggle('kr--has-more', shown.length > limit);
      if (this.empty) this.empty.hidden = shown.length > 0;
      this.querySelectorAll('[data-kr-filter]').forEach(chip => {
        const type = chip.dataset.krFilter;
        chip.setAttribute('aria-pressed', String(type === 'all' ? !this.state.types.size : this.state.types.has(type)));
      });
      this.querySelectorAll('[data-kr-star]').forEach(bar => bar.setAttribute('aria-pressed', String(bar.dataset.krStar === this.state.star)));
      const rating = this.querySelector('[data-kr-rating]');
      if (rating) rating.value = this.state.star;
      if (this.status && !first) {
        const count = shown.filter(card => !card.hasAttribute('data-kr-editor')).length;
        this.status.textContent = (count === 1 ? this.dataset.resultsOne : this.dataset.resultsOther).replace('__N__', count);
      }
    }

    loadMore() {
      const before = this.limit;
      this.limit += this.step;
      this.apply();
      // Keyboard users continue at the first newly shown review.
      const next = [...this.grid.children].filter(card => !card.hidden)[before];
      if (next) { next.tabIndex = -1; next.focus({ preventScroll: false }); }
    }

    // Theme Editor: a selected review block always becomes visible, even with filters or pagination.
    reveal(card) {
      if (!card) return;
      if (card.hidden) {
        this.state.types.clear(); this.state.star = ''; this.state.query = '';
        const search = this.querySelector('[data-kr-search]');
        if (search) search.value = '';
        this.limit = this.cards.length;
        this.apply();
      }
      card.classList.add('is-in');
      card.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' });
    }

    // Link from the review-request email: ?review=1 (or #review-schrijven) opens the form, ?naam= fills the first name.
    openFromLink() {
      if (!this.formDialog || document.querySelector('kyo-reviews') !== this) return;
      const params = new URLSearchParams(location.search);
      if (!params.has('review') && location.hash !== '#review-schrijven') return;
      if (params.has('contact_posted') || this.formDialog.open) return;
      const name = (params.get('naam') || '').trim().slice(0, 60);
      const field = this.formDialog.querySelector('[name="contact[name]"]');
      if (name && field && !field.value) field.value = name;
      this.scrollIntoView({ block: 'start' });
      this.openForm(null);
    }

    openForm(opener) {
      if (!this.formDialog || this.formDialog.open) return;
      this.writeOpener = opener;
      this.formDialog.classList.remove('is-submitting');
      const form = this.formDialog.querySelector('form');
      if (form) form.dataset.krStarted = String(Date.now());
      this.formDialog.showModal();
      // Shopify's storefront script binds its invisible hCaptcha to a contact form on focus: bind it early.
      this.formDialog.querySelector('[name="contact[email]"]')?.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
    }

    // Review API (tools/review-api): the review and its files go straight to Shopify and are online at once.
    // If the API cannot be reached, the form falls back to Shopify's contact form, so no review is lost.
    setupApi() {
      const form = this.formDialog?.querySelector('form');
      if (!form?.dataset.krApi) return;
      this.addEventListener('submit', event => {
        if (event.target !== form || form.dataset.krApiFailed) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitToApi(form);
      }, true);
      this.formDialog.addEventListener('close', () => { if (this.reviewHandle) this.refreshSection(this.reviewHandle); });
    }

    async submitToApi(form) {
      if (form.dataset.krSending) return;
      const status = form.querySelector('[data-kr-form-status]');
      const submit = form.querySelector('[type="submit"]');
      const field = name => form.querySelector(`[name="${name}"]`);
      const data = new FormData();
      data.append('rating', (form.querySelector('[name="contact[Sterren]"]:checked')?.value || '').split('/')[0]);
      data.append('name', field('contact[name]')?.value || '');
      data.append('email', field('contact[email]')?.value || '');
      data.append('title', field('contact[Titel]')?.value || '');
      data.append('body', field('contact[body]')?.value || '');
      data.append('consent', field('contact[Toestemming]')?.checked ? 'yes' : '');
      data.append('kr_trap', field('kr_trap')?.value || '');
      data.append('started', form.dataset.krStarted || '0');
      (this.files || []).forEach(file => data.append('files', file, file.name));
      form.dataset.krSending = '1';
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      status.textContent = form.dataset.textSending;
      try {
        const response = await fetch(`${form.dataset.krApi.replace(/\/+$/, '')}/reviews`, { method: 'POST', body: data });
        const result = await response.json().catch(() => ({}));
        if (response.ok && result.ok) {
          status.textContent = '';
          this.reviewHandle = result.handle;
          form.classList.add('is-done');
          const done = form.querySelector('[data-kr-api-done]');
          if (result.queued) {
            // Stored safely, goes online as soon as Shopify accepts it (retried hourly).
            done.querySelector('h3').textContent = form.dataset.textQueuedTitle;
            done.querySelector('p').textContent = form.dataset.textQueued;
          }
          done.hidden = false;
          form.querySelector('[data-kr-api-done] [data-kr-form-close]')?.focus();
          return;
        }
        if (response.status >= 500) throw new Error('server');
        const code = result.error;
        status.textContent = code === 'rate_limited' ? form.dataset.textRate
          : /^file_|too_many_files/.test(code || '') ? form.dataset.textFile.replace('{size}', form.dataset.krMaxMb || '50')
          : form.dataset.textGeneric;
      } catch (error) {
        // API unreachable or broken: send through Shopify's contact form instead.
        form.dataset.krApiFailed = '1';
        status.textContent = '';
        delete form.dataset.krSending;
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        form.requestSubmit(submit);
        return;
      }
      delete form.dataset.krSending;
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }

    // Re-render this section (Section Rendering API) until the new review is in, so it shows without a reload.
    async refreshSection(handle, attempt = 0) {
      const id = this.dataset.sectionId;
      const wrapper = document.getElementById(`shopify-section-${id}`);
      if (!id || !wrapper) return;
      try {
        const url = new URL(location.href);
        url.searchParams.set('section_id', id);
        url.searchParams.set('kr', Date.now());
        const html = await (await fetch(url, { headers: { Accept: 'text/html' } })).text();
        if (!html.includes(handle) && attempt < 5) { setTimeout(() => this.refreshSection(handle, attempt + 1), 1500); return; }
        const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
        if (fresh) wrapper.replaceChildren(...fresh.childNodes);
      } catch (error) { /* keep the current wall */ }
    }

    // Same flow as the newsletter pop-up (kyo-popup.js): wait for the captcha if it is still loading, and leave
    // modal mode while posting, because a modal dialog sits above hCaptcha's challenge and would block it.
    setupCaptcha() {
      const form = this.formDialog?.querySelector('form');
      if (!form) return;
      this.addEventListener('submit', event => {
        if (event.target !== form) return;
        if (!form.dataset.hcaptchaBound && !form.dataset.recaptchaBound && form.dataset.cptcha && !form.dataset.krWaited) {
          event.preventDefault();
          form.dataset.krWaited = '1';
          const started = Date.now();
          const wait = setInterval(() => {
            if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound || Date.now() - started > 8000) { clearInterval(wait); form.requestSubmit(); }
          }, 150);
          return;
        }
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('aria-busy', 'true');
        setTimeout(() => { submit?.removeAttribute('aria-busy'); delete form.dataset.krWaited; }, 4000);
        if (this.formDialog.matches(':modal')) {
          const y = scrollY;
          this.formDialog.close();
          this.formDialog.classList.add('is-submitting');
          this.formDialog.show();
          scrollTo(0, y);
        }
      });
    }

    // Photo/video uploads. Shopify's contact form cannot carry files, but Shopify does store files sent as
    // cart line item properties on its CDN. Each file is added with the product to the cart (properties prefixed
    // with _ stay hidden), the CDN link is read from the response and the line is removed straight away.
    // The links go into the contact form as 'Foto of video'.
    setupUploads() {
      const form = this.formDialog?.querySelector('form');
      const input = form?.querySelector('[data-kr-files]');
      if (!input) return;
      this.files = [];
      const list = form.querySelector('[data-kr-file-list]');
      const status = form.querySelector('[data-kr-upload-status]');
      const maxBytes = (Number(form.dataset.krMaxMb) || 20) * 1048576;
      const say = text => { status.textContent = text; };
      const render = () => {
        list.replaceChildren(...this.files.map((file, index) => {
          const item = document.createElement('li');
          const name = document.createElement('span');
          name.textContent = `${file.name} · ${file.size < 1048576 ? `${Math.max(1, Math.round(file.size / 1024))} KB` : `${(file.size / 1048576).toFixed(1)} MB`}`;
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.textContent = '×';
          remove.setAttribute('aria-label', `${form.dataset.textRemove}: ${file.name}`);
          remove.addEventListener('click', () => { this.files.splice(index, 1); render(); say(''); input.focus(); });
          if (file.type.startsWith('image/')) {
            const thumb = document.createElement('img');
            thumb.alt = '';
            thumb.src = URL.createObjectURL(file);
            thumb.onload = () => URL.revokeObjectURL(thumb.src);
            item.append(thumb);
          }
          item.append(name, remove);
          return item;
        }));
      };
      input.addEventListener('change', () => {
        say('');
        for (const file of input.files) {
          if (this.files.length >= 3) { say(form.dataset.textMany); break; }
          if (file.size > maxBytes) { say(form.dataset.textBig.replace('{name}', file.name).replace('{size}', form.dataset.krMaxMb)); continue; }
          this.files.push(file);
        }
        input.value = '';
        render();
      });
      // Capture phase: runs before Shopify's own submit handling (spam check), which then runs on the resubmit.
      this.addEventListener('submit', async event => {
        if (event.target !== form || form.dataset.krUploaded || !this.files.length) return;
        // With the review API the files are sent along with the review; the cart route is only the fallback.
        if (form.dataset.krApi && !form.dataset.krApiFailed) return;
        if (!form.dataset.krVariant) { this.files = []; return; }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (form.dataset.krUploading) return;
        form.dataset.krUploading = '1';
        const submit = form.querySelector('[type="submit"]');
        submit.disabled = true;
        try {
          const urls = [];
          for (const [index, file] of this.files.entries()) {
            say(form.dataset.textBusy.replace('{n}', index + 1).replace('{total}', this.files.length));
            urls.push(await this.uploadFile(form, file));
          }
          form.querySelector('[data-kr-file-urls]').value = urls.join('\n');
          form.dataset.krUploaded = '1';
          say('');
          submit.disabled = false;
          form.requestSubmit(submit);
        } catch (error) {
          say(form.dataset.textFailed);
          submit.disabled = false;
        } finally {
          delete form.dataset.krUploading;
        }
      }, true);
    }

    async uploadFile(form, file) {
      const root = form.dataset.krRoot || '/';
      const data = new FormData();
      data.append('id', form.dataset.krVariant);
      data.append('quantity', '1');
      data.append('properties[_Review upload]', file, file.name);
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('upload');
      const item = await response.json();
      const url = Object.values(item.properties || {}).find(value => typeof value === 'string' && /^https?:\/\//.test(value));
      await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
      if (!url) throw new Error('upload');
      return url;
    }

    // The first review section on the page is the source for the rating in the buy box (kyo-social-proof).
    shareStats() {
      if (document.querySelector('kyo-reviews') !== this) return;
      const stats = { count: Number(this.dataset.reviewCount) || 0, average: Number(this.dataset.reviewAverage) || 0, target: `#${this.id}` };
      window.kyoReviewStats = stats;
      document.dispatchEvent(new CustomEvent('kyo:review-stats', { detail: stats }));
    }

    // "2 maanden geleden" in the page language; the full date stays in the tooltip and datetime attribute.
    formatDates() {
      if (this.dataset.dateStyle !== 'relative' || !window.Intl?.RelativeTimeFormat) return;
      const format = new Intl.RelativeTimeFormat(document.documentElement.lang || 'nl', { numeric: 'auto' });
      this.querySelectorAll('[data-kr-date]').forEach(element => {
        const time = Date.parse(element.getAttribute('datetime'));
        if (Number.isNaN(time)) return;
        const days = Math.round((time - Date.now()) / 864e5);
        const size = Math.abs(days);
        const [value, unit] = size < 7 ? [days, 'day'] : size < 30 ? [Math.round(days / 7), 'week'] : size < 365 ? [Math.round(days / 30.4), 'month'] : [Math.round(days / 365), 'year'];
        element.title = element.textContent.trim();
        element.textContent = format.format(value, unit);
      });
    }

    restoreHelpful() {
      this.querySelectorAll('[data-kr-helpful]').forEach(button => {
        if (store.get(`kyo-review-helpful:${button.dataset.krHelpful}`)) this.markHelpful(button);
      });
    }

    markHelpful(button) {
      if (button.getAttribute('aria-pressed') === 'true') return;
      const card = button.closest('[data-kr-card]');
      const count = Number(card.dataset.helpful) + 1;
      card.dataset.helpful = count;
      button.querySelector('[data-kr-helpful-count]').textContent = count;
      button.setAttribute('aria-pressed', 'true');
    }

    // Votes are remembered in this browser only; the stored count is the one set in Customize.
    vote(button) {
      if (button.getAttribute('aria-pressed') === 'true') return;
      store.set(`kyo-review-helpful:${button.dataset.krHelpful}`, '1');
      this.markHelpful(button);
      const api = this.formDialog?.querySelector('form')?.dataset.krApi;
      if (api && button.dataset.krHandle) {
        fetch(`${api.replace(/\/+$/, '')}/helpful`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle: button.dataset.krHandle }) }).catch(() => {});
      }
    }

    openVideo(button) {
      const next = button.nextElementSibling;
      const template = next?.matches('template[data-kyo-video-template]') ? next : button.parentElement.querySelector('template[data-kyo-video-template]');
      const stage = this.dialog?.querySelector('[data-kr-dialog-stage]');
      if (!template || !stage || this.dialog.open) return;
      stage.replaceChildren(template.content.cloneNode(true));
      const video = stage.querySelector('video');
      if (!video) return;
      if (template.dataset.label) { video.setAttribute('aria-label', template.dataset.label); this.dialog.setAttribute('aria-label', template.dataset.label); }
      video.controls = true;
      video.playsInline = true;
      video.preload = 'auto';
      this.dialog.style.setProperty('--kr-video-ratio', button.dataset.ratio || 0.5625);
      this.dialog.classList.toggle('kr-dialog--landscape', Number(button.dataset.ratio) > 1);
      this.opener = button;
      this.dialog.showModal();
      video.muted = false;
      video.play().catch(() => {});
    }

    closeVideo() {
      const video = this.dialog.querySelector('video');
      if (video) { video.pause(); video.remove(); }
      this.opener?.focus({ preventScroll: true });
    }

    // Cards and rating bars ease in once when they scroll into view (skipped with reduced motion).
    observeEntrance() {
      if (reduced.matches || !('IntersectionObserver' in window)) { this.classList.add('kr--static'); return; }
      this.classList.add('kr--animate');
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        this.observer.unobserve(entry.target);
      }), { rootMargin: '0px 0px -8% 0px' });
      this.querySelectorAll('[data-kr-card], .kr-summary').forEach(element => this.observer.observe(element));
    }
  }

  customElements.define('kyo-reviews', KyoReviews);
})();
